// src/components/vault/utils/requirementGrouping.ts
//
// Shared constants and grouping logic for vault requirements.
// Used by VaultSidebar (visual grouping) and VaultPageRedesign (navigation order)
// to ensure both always agree on which requirements are "identity" vs "additional".

import {
  Status,
  Type,
  type Folder,
  type Requirement,
} from "../../../redux/interfaces/folder.types";
import type { BdcStatusResponse } from "../../../api/bdc.api";
import { isIdentityRequirementComplete } from "../../../utils/vault/isIdentityRequirementComplete";

/** Slugs that represent the ID requirement */
export const ID_REQUIREMENT_SLUGS = ["official-id", "identificacion-oficial"];

/** Slugs that represent the proof of address requirement */
export const PROOF_OF_ADDRESS_SLUGS = [
  "proof-of-address",
  "comprobante-domicilio",
];

/** Slug for the virtual data validation step */
export const DATA_VALIDATION_SLUG = "data-validation";

/** BDC review that leaves an approved person form unchanged. */
export const BDC_CONTACT_REVIEW_SLUG = "bdc-contact-review";

/** Person forms populated by the identity confirmation flow. */
export const PERSON_FORM_SLUGS = [
  "tenant-form",
  "tenant-cja-form",
  "guarantor-form",
  "guarantor-cja-form",
  "landlord-form",
  "legal-rep-form",
  "legal-rep-short-form",
  "owner-representative-form",
];

export function hasValidatedPersonForm(
  requirements: Array<Pick<Requirement, "slug" | "status">>,
): boolean {
  return requirements.some(
    (requirement) =>
      PERSON_FORM_SLUGS.includes(requirement.slug) &&
      requirement.status === Status.Valid,
  );
}

export function isIdentityRequirement(requirement: {
  readonly slug: string;
  readonly type: string;
}): boolean {
  return (
    ID_REQUIREMENT_SLUGS.includes(requirement.slug) ||
    ID_REQUIREMENT_SLUGS.includes(requirement.type) ||
    requirement.type === Type.PhotoID
  );
}

export function isProofOfAddressRequirement(requirement: {
  readonly slug: string;
  readonly type: string;
}): boolean {
  return (
    PROOF_OF_ADDRESS_SLUGS.includes(requirement.slug) ||
    PROOF_OF_ADDRESS_SLUGS.includes(requirement.type) ||
    requirement.type === Type.ProofOfAddress
  );
}

export function isProofOfAddressProcessing(
  requirement: RequirementIdentifier | null | undefined,
): boolean {
  return Boolean(
    requirement &&
      isProofOfAddressRequirement(requirement) &&
      requirement.status === Status.PendingReview &&
      requirement.workflow_step === "validating",
  );
}

export function isIdentityRequirementProcessing(
  requirement: Requirement | null | undefined,
): boolean {
  return Boolean(
    requirement &&
      isIdentityRequirement(requirement) &&
      requirement.status === Status.PendingReview &&
      requirement.workflow_step === "validating",
  );
}

export function isRequirementProcessing(
  requirement: Requirement | null | undefined,
): boolean {
  return (
    isIdentityRequirementProcessing(requirement) ||
    isProofOfAddressProcessing(requirement)
  );
}

export function getRequirementProcessingLabel(
  requirement: Requirement,
): string | null {
  if (isIdentityRequirementProcessing(requirement)) {
    return "Validando identificación";
  }
  if (isProofOfAddressProcessing(requirement)) {
    return "Analizando comprobante";
  }
  return null;
}

/**
 * Preserve develop's non-blocking treatment of terminal pending_review records,
 * while excluding the transient state introduced by asynchronous POA OCR.
 */
export function isRequirementVisuallyComplete(
  requirement: Requirement | null | undefined,
): boolean {
  if (!requirement) return false;
  if (isIdentityRequirement(requirement)) {
    return isIdentityRequirementComplete(requirement);
  }

  return Boolean(
    (requirement.status === Status.Valid ||
      requirement.status === Status.PendingReview) &&
      !isProofOfAddressProcessing(requirement),
  );
}

export function folderHasProcessingProofOfAddress(
  folder: Folder | undefined,
): boolean {
  return Boolean(
    folder?.subfolders?.some((subfolder) =>
      subfolder.requirements.some(isProofOfAddressProcessing),
    ),
  );
}

export function shouldKeepProofOfAddressActive(
  activeRequirement: Requirement | null | undefined,
  proofOfAddressRequirement: Requirement | null | undefined,
): boolean {
  if (
    (activeRequirement?.slug !== DATA_VALIDATION_SLUG &&
      activeRequirement?.slug !== BDC_CONTACT_REVIEW_SLUG) ||
    !proofOfAddressRequirement
  ) {
    return false;
  }
  return (
    proofOfAddressRequirement.status === Status.Invalid ||
    isProofOfAddressProcessing(proofOfAddressRequirement)
  );
}

interface RequirementIdentifier {
  readonly id?: string;
  readonly slug: string;
  readonly type: string;
  readonly status?: string;
  readonly workflow_step?: string | null;
  readonly is_native_bdc_run?: boolean;
  readonly verification_data?: Requirement["verification_data"];
}

export interface RequirementLockContext<
  TRequirement extends RequirementIdentifier = RequirementIdentifier,
> {
  readonly requirements: readonly TRequirement[];
  readonly lockedFormSlugs: readonly string[] | null;
  readonly bdcPrerequisiteSlugs?: readonly string[] | null;
  readonly isIDCompleted: boolean;
  readonly isProofOfAddressCompleted: boolean;
  readonly isDataValidationCompleted: boolean;
  readonly isBdcDestinationReviewed?: boolean;
  /** Requirement whose current actor-scoped GET verified an existing Buró run. */
  readonly existingBdcRunRequirementId?: string | null;
  /** Completed legacy report preserves its pre-migration signature prerequisites. */
  readonly legacyCompletedBdcRequirementId?: string | null;
  /** Old persisted approvals must not apply while a replacement folder is loading. */
  readonly bdcReportReplacementRequirementId?: string | null;
  readonly bdcAuthoritativeRequirementStatus?: string;
  readonly isAddressConfirmedUpdated: boolean;
}

export function getVaultRequirementLockedDescription(
  requirement: Requirement,
  context: RequirementLockContext<Requirement>,
): string {
  if (isProofOfAddressRequirement(requirement)) {
    return "Completa la identificación primero";
  }

  if (
    (requirement.slug === DATA_VALIDATION_SLUG ||
      requirement.slug === BDC_CONTACT_REVIEW_SLUG) &&
    context.requirements.some(isProofOfAddressProcessing)
  ) {
    return "Estamos validando tu comprobante";
  }

  return "Completa los pasos anteriores primero";
}

export const BDC_AUTHORIZATION_SLUGS: readonly string[] = [
  "bdc-authorization",
  "autorizacion-bdc",
];

export function isLegacyBdcCaptureRecoveryEligible(
  requirement: RequirementIdentifier | undefined,
  status: BdcStatusResponse | undefined,
): boolean {
  return Boolean(
    requirement?.status === Status.Invalid &&
      status?.legacy_capture_recovery_eligible === true &&
      status.is_native_bdc_run === false &&
      status.sic_source === "buro_de_credito" &&
      status.is_active === true &&
      status.status === "capturing_inputs",
  );
}

/**
 * Resolve the BDC source using the same precedence as the backend: a source
 * included in the live status response wins, including an explicit null.
 * Persisted metadata is only a fallback when the response omits the field.
 */
export function getBdcSicSource(
  status: BdcStatusResponse | undefined,
  requirement: Requirement | undefined,
): string | null | undefined {
  if (status && Object.prototype.hasOwnProperty.call(status, "sic_source")) {
    return status.sic_source;
  }
  return requirement?.verification_data?.bdc?.sic_source;
}

/** The company input limits apply only while a mutable native Buró run is capturing data. */
export function shouldValidateNativeBdcCompanyData(
  requirement: Requirement | undefined,
  status: BdcStatusResponse | undefined,
): boolean {
  const persisted = requirement?.verification_data?.bdc;
  const isTerminalStatus = (value: unknown) =>
    value === Status.Valid || value === Status.PendingReview;

  return Boolean(
    requirement &&
      getBdcSicSource(status, requirement) === "buro_de_credito" &&
      status?.status === "capturing_inputs" &&
      !status.start_recovery_pending &&
      !persisted?.inputs_consumed_at &&
      !persisted?.start_recovery_pending &&
      (!persisted?.last_status || persisted.last_status === "capturing_inputs") &&
      !isTerminalStatus(requirement.status) &&
      !isTerminalStatus(status?.requirement_status),
  );
}

/** Resuming a verified run never creates consent or completes a local step. */
export function hasVerifiedExistingBdcRun(
  requirement: RequirementIdentifier | undefined,
  status: BdcStatusResponse | undefined,
): boolean {
  const isRejectedResult =
    status?.sic_source === "buro_de_credito" &&
    status.requirement_status === Status.Invalid &&
    (status.status === "invalid" || status.status === "completed");
  return Boolean(
    requirement?.id &&
      (requirement.status !== Status.Invalid || isRejectedResult) &&
      (status?.requirement_status !== Status.Invalid || isRejectedResult) &&
      status &&
      ["buro_de_credito", "circulo_de_credito"].includes(
        status.sic_source ?? "",
      ) &&
      status.is_active === true &&
      (["pin_validation", "long_report", "in_review", "completed"].includes(
        status.status,
      ) ||
        (status.sic_source === "buro_de_credito" &&
          status.status === "recovering" &&
          status.start_recovery_pending === true) ||
        isRejectedResult),
  );
}

/** Only a current mutable Buró report may accept a fresh identity snapshot. */
export function canCaptureBdcIdentity(
  requirement: Requirement | undefined,
  status: BdcStatusResponse | undefined,
): boolean {
  const persisted = requirement?.verification_data?.bdc;
  return Boolean(
    requirement?.status === Status.PendingUpload &&
      status?.sic_source === "buro_de_credito" &&
      status.is_active === true &&
      status.status === "capturing_inputs" &&
      !status.start_recovery_pending &&
      !persisted?.inputs_consumed_at &&
      !persisted?.start_recovery_pending &&
      (!persisted?.last_status || persisted.last_status === "capturing_inputs"),
  );
}

/** A fresh mutable report must refresh the folder before reusing any approval. */
export function isBdcReportReplacementPending(
  requirement: Requirement | undefined,
  status: BdcStatusResponse | undefined,
): boolean {
  const persistedBdc = requirement?.verification_data?.bdc;
  return Boolean(
    requirement &&
      status?.requirement_status === Status.PendingUpload &&
      (requirement.status === Status.Valid ||
        requirement.status === Status.PendingReview ||
        (status.status === "capturing_inputs" &&
          (persistedBdc?.inputs_consumed_at ||
            (persistedBdc?.last_status &&
              persistedBdc.last_status !== "capturing_inputs")))),
  );
}

export const BDC_COMPANY_DATA_SLUG = "bdc-company-data";

/** Navigation and recovery must point at the same virtual PM step. */
export function createBdcCompanyDataRequirement(
  bdcRequirement: Requirement,
  status: Status,
): Requirement {
  return {
    id: `virtual-bdc-company-data-${bdcRequirement.id}`,
    name: "Datos de la Empresa para Buró de Crédito",
    slug: BDC_COMPANY_DATA_SLUG,
    type: Type.Form,
    status,
    subtype: null,
    files: [],
    subfolder: bdcRequirement.subfolder,
  };
}

export const BDC_COMPANY_DATA_SLUGS: readonly string[] = [
  BDC_COMPANY_DATA_SLUG,
];

export const DIGITAL_SIGNATURE_SLUGS: readonly string[] = [
  "digital-signature",
  "firma-digital",
];

/**
 * Priority ordering within the "additional" group.
 * Lower number = appears first. Unrecognized slugs default to 50.
 */
const ADDITIONAL_PRIORITY: Record<string, number> = {
  // Person forms first
  "tenant-form": 10,
  "formulario-inquilino": 10,
  "formulario-del-inquilino": 10,
  "guarantor-form": 11,
  "formulario-fiador": 11,
  "formulario-obligado-solidario": 11,
  "owner-representative-form": 12,
  "formulario-representante-propietario": 12,
  // Income proofs after forms
  "proof-of-income": 20,
  "comprobante-ingresos": 20,
  "comprobante-de-ingresos": 20,
  // References
  "personal-references": 30,
  "referencias-personales": 30,
  "digital-signature": 41,
  "firma-digital": 41,
};

const IDENTITY_PRIORITY: Record<string, number> = {
  "official-id": 10,
  "identificacion-oficial": 10,
  "proof-of-address": 20,
  "comprobante-domicilio": 20,
  [DATA_VALIDATION_SLUG]: 30,
  [BDC_CONTACT_REVIEW_SLUG]: 30,
  [BDC_COMPANY_DATA_SLUG]: 35,
  "bdc-authorization": 40,
  "autorizacion-bdc": 40,
  "digital-signature": 41,
  "firma-digital": 41,
};

const DEFAULT_PRIORITY = 50;

export type RequirementGroup = "identity" | "additional";

export function isBdcAuthorizationRequirement(
  slug: string,
  type: string,
): boolean {
  return (
    BDC_AUTHORIZATION_SLUGS.includes(slug) ||
    BDC_AUTHORIZATION_SLUGS.includes(type)
  );
}

export function isBdcCompanyDataRequirement(
  slug: string,
  type: string,
): boolean {
  return (
    BDC_COMPANY_DATA_SLUGS.includes(slug) ||
    BDC_COMPANY_DATA_SLUGS.includes(type)
  );
}

function isDigitalSignatureRequirement(slug: string, type: string): boolean {
  return (
    DIGITAL_SIGNATURE_SLUGS.includes(slug) ||
    DIGITAL_SIGNATURE_SLUGS.includes(type)
  );
}

function getBdcAuthorizationRequirement(
  requirements: readonly RequirementIdentifier[],
): RequirementIdentifier | undefined {
  return requirements.find((requirement) =>
    isBdcAuthorizationRequirement(requirement.slug, requirement.type),
  );
}

/**
 * A finished native Buró run already consumed the confirmed identity snapshot,
 * so a later loss of `confirmed_data` (staff rejection, re-upload) must not
 * keep the signature locked.
 */
function hasCompletedNativeBdcRun(
  bdcRequirement: RequirementIdentifier,
): boolean {
  const persisted = bdcRequirement.verification_data?.bdc;
  return Boolean(
    bdcRequirement.status === Status.Valid &&
      bdcRequirement.is_native_bdc_run === true &&
      persisted?.last_status === "completed" &&
      persisted?.inputs_consumed_at,
  );
}

function getRequirementBySlugOrType(
  requirements: readonly RequirementIdentifier[],
  slugs: readonly string[],
): RequirementIdentifier | undefined {
  return requirements.find(
    (requirement) =>
      slugs.includes(requirement.slug) || slugs.includes(requirement.type),
  );
}

function isRequirementCompleted(requirement: RequirementIdentifier): boolean {
  return (
    requirement.status === "valid" || requirement.status === "pending_review"
  );
}

export function hasBlockingBdcPrerequisites(
  requirements: readonly RequirementIdentifier[],
  prerequisiteSlugs: readonly string[] | null | undefined,
): boolean {
  return (prerequisiteSlugs ?? []).some((prerequisiteSlug) => {
    const prerequisite = getRequirementBySlugOrType(requirements, [
      prerequisiteSlug,
    ]);
    return !prerequisite || !isRequirementCompleted(prerequisite);
  });
}

function getIdentityPriority(
  requirement: RequirementIdentifier,
  lockedFormSlugs: readonly string[] | null,
): number {
  if (lockedFormSlugs?.includes(requirement.slug)) return 45;
  if (isIdentityRequirement(requirement)) return 10;
  if (isProofOfAddressRequirement(requirement)) return 20;
  return (
    IDENTITY_PRIORITY[requirement.slug] ??
    IDENTITY_PRIORITY[requirement.type] ??
    DEFAULT_PRIORITY
  );
}

/**
 * Classify a requirement into its visual group.
 *
 * "identity" = Verificación de identidad (ID, proof of address, confirmed values, BDC)
 * "additional" = Documentos adicionales (everything else)
 */
export function getRequirementGroup(
  slug: string,
  type: string,
  lockedFormSlugs: string[] | null,
  hasBdcAuthorization = false,
): RequirementGroup {
  if (
    ID_REQUIREMENT_SLUGS.includes(slug) ||
    ID_REQUIREMENT_SLUGS.includes(type) ||
    (hasBdcAuthorization && type === Type.PhotoID)
  ) {
    return "identity";
  }
  if (
    PROOF_OF_ADDRESS_SLUGS.includes(slug) ||
    PROOF_OF_ADDRESS_SLUGS.includes(type) ||
    (hasBdcAuthorization && type === Type.ProofOfAddress)
  ) {
    return "identity";
  }
  if (slug === DATA_VALIDATION_SLUG || slug === BDC_CONTACT_REVIEW_SLUG) {
    return "identity";
  }
  if (isBdcAuthorizationRequirement(slug, type)) {
    return "identity";
  }
  if (isBdcCompanyDataRequirement(slug, type)) {
    return "identity";
  }
  if (hasBdcAuthorization && isDigitalSignatureRequirement(slug, type)) {
    return "identity";
  }
  if (lockedFormSlugs && lockedFormSlugs.includes(slug)) {
    return "identity";
  }

  return "additional";
}

/** Shared prerequisite rules for desktop, mobile and navigation guards. */
export function isRequirementLockedByPrerequisites<
  TRequirement extends RequirementIdentifier,
>(
  requirement: TRequirement,
  context: RequirementLockContext<TRequirement>,
): boolean {
  const { slug, type } = requirement;
  const bdcRequirement = getBdcAuthorizationRequirement(context.requirements);
  const isBdcContext =
    Boolean(bdcRequirement) ||
    slug === BDC_CONTACT_REVIEW_SLUG ||
    isBdcCompanyDataRequirement(slug, type) ||
    isBdcAuthorizationRequirement(slug, type);
  const proofOfAddressRequirement = context.requirements.find(
    isProofOfAddressRequirement,
  );
  const isProofOfAddressStillProcessing = isProofOfAddressProcessing(
    proofOfAddressRequirement,
  );
  const areIdentityDataPrerequisitesBlocked =
    !context.isIDCompleted ||
    Boolean(
      proofOfAddressRequirement &&
        ((isBdcContext &&
          (isProofOfAddressStillProcessing ||
            proofOfAddressRequirement.status === Status.Invalid)) ||
          (!context.isAddressConfirmedUpdated &&
            !context.isProofOfAddressCompleted)),
    );

  if (isIdentityRequirement(requirement)) {
    return false;
  }

  if (isProofOfAddressRequirement(requirement)) {
    return !context.isIDCompleted;
  }

  if (slug === DATA_VALIDATION_SLUG || slug === BDC_CONTACT_REVIEW_SLUG) {
    return (
      areIdentityDataPrerequisitesBlocked || isProofOfAddressStillProcessing
    );
  }

  if (isBdcCompanyDataRequirement(slug, type)) {
    if (areIdentityDataPrerequisitesBlocked) return true;
    return !context.isDataValidationCompleted;
  }

  if (isBdcAuthorizationRequirement(slug, type)) {
    // Rejected or processing identity/address documents remain hard blockers.
    if (
      !context.isIDCompleted ||
      isProofOfAddressStillProcessing ||
      proofOfAddressRequirement?.status === Status.Invalid ||
      (requirement.id &&
        context.bdcReportReplacementRequirementId === requirement.id)
    )
      return true;
    // A terminal card is read-only even while POA awaits staff approval.
    // Start and signature continue to use their full prerequisite gates.
    if (
      requirement.status === Status.Valid ||
      requirement.status === Status.PendingReview ||
      (requirement.id &&
        context.existingBdcRunRequirementId === requirement.id &&
        context.bdcAuthoritativeRequirementStatus === Status.Invalid)
    )
      return false;
    if (
      requirement.id &&
      context.existingBdcRunRequirementId === requirement.id
    )
      return false;
    if (areIdentityDataPrerequisitesBlocked) return true;
    if (!context.isDataValidationCompleted) return true;
    if (context.isBdcDestinationReviewed === false) return true;

    return hasBlockingBdcPrerequisites(
      context.requirements,
      context.bdcPrerequisiteSlugs,
    );
  }

  if (isDigitalSignatureRequirement(slug, type)) {
    if (!bdcRequirement) return false;
    if (
      bdcRequirement.id &&
      context.bdcReportReplacementRequirementId === bdcRequirement.id
    )
      return true;

    if (areIdentityDataPrerequisitesBlocked) return true;
    // Legacy completion did not require the newer confirmed-data snapshot.
    // Keep identity/address and any explicitly configured form locks intact.
    if (
      !context.isDataValidationCompleted &&
      (context.lockedFormSlugs?.includes(slug) ||
        (!(
          hasCompletedNativeBdcRun(bdcRequirement) &&
          context.existingBdcRunRequirementId === bdcRequirement.id
        ) &&
          (!bdcRequirement.id ||
            context.legacyCompletedBdcRequirementId !== bdcRequirement.id)))
    )
      return true;

    // An ambiguous BDC result is persisted as pending_review so the actor can
    // leave the BDC card, but it must not unlock the legal signature step.
    return (
      bdcRequirement.status !== Status.Valid ||
      (context.bdcAuthoritativeRequirementStatus !== undefined &&
        context.bdcAuthoritativeRequirementStatus !== Status.Valid)
    );
  }

  if (context.lockedFormSlugs?.includes(slug)) {
    if (areIdentityDataPrerequisitesBlocked) return true;

    if (proofOfAddressRequirement) {
      return !context.isDataValidationCompleted;
    }

    return false;
  }

  return false;
}

/**
 * Reorder requirements so identity group comes first, then additional.
 * Preserves the original order within each group.
 */
export function sortRequirementsByGroup<
  T extends { slug: string; type: string },
>(requirements: T[], lockedFormSlugs: string[] | null): T[] {
  const identity: T[] = [];
  const additional: T[] = [];
  const hasBdcAuthorization = requirements.some((requirement) =>
    isBdcAuthorizationRequirement(requirement.slug, requirement.type),
  );

  for (const req of requirements) {
    if (
      getRequirementGroup(
        req.slug,
        req.type,
        lockedFormSlugs,
        hasBdcAuthorization,
      ) === "identity"
    ) {
      identity.push(req);
    } else {
      additional.push(req);
    }
  }

  if (hasBdcAuthorization) {
    identity.sort((a, b) => {
      const pa = getIdentityPriority(a, lockedFormSlugs);
      const pb = getIdentityPriority(b, lockedFormSlugs);
      return pa - pb;
    });
  }

  // Sub-sort within the additional group by explicit priority
  additional.sort((a, b) => {
    const pa = ADDITIONAL_PRIORITY[a.slug] ?? DEFAULT_PRIORITY;
    const pb = ADDITIONAL_PRIORITY[b.slug] ?? DEFAULT_PRIORITY;
    return pa - pb;
  });

  return [...identity, ...additional];
}
